import apiClient from './client';
import { API } from './endpoints';

/**
 * The login check (DatabaseUpdateController): is this connection's database at
 * the schema the API build expects? The API answers in a couple of queries; a
 * database that is behind is updated in the background while the website shows
 * "Updating your database" and polls Status.
 */

/** Ensure: UpToDate / Disabled / Unavailable mean "go in"; Running means wait. */
export type EnsureState = 'UpToDate' | 'Disabled' | 'Unavailable' | 'Running';

/** Status adds how a run ended. Abandoned = its server stopped; the next check resumes it. */
export type RunState =
  | EnsureState
  | 'Completed'
  | 'CompletedWithErrors'
  | 'Failed'
  | 'Abandoned';

export interface DatabaseUpdateScriptResult {
  lane: string;
  script: string;
  status: string;
  error?: string | null;
  durationMs: number;
  attempts: number;
  timestampUtc: string;
}

export interface DatabaseUpdateRun {
  state: RunState;
  runId?: number;
  step?: number;
  stepCount?: number;
  applied?: number;
  failed?: number;
  trigger?: string;
  /** True when this call started the run; false when someone else did. */
  startedHere?: boolean | null;
  startedUtc?: string;
  completedUtc?: string | null;
  elapsedMs?: number;
  /** PSI staff only (null for everyone else): the API leaves out script names and errors. */
  currentScript?: string | null;
  firstError?: string | null;
  results?: DatabaseUpdateScriptResult[] | null;
}

/** Terminal states that let the user straight in. */
export const PASS_STATES: ReadonlySet<RunState> = new Set<RunState>(['UpToDate', 'Disabled', 'Unavailable', 'Completed']);
/** Terminal states that show "The update didn't finish". */
export const FAILED_STATES: ReadonlySet<RunState> = new Set<RunState>(['CompletedWithErrors', 'Failed']);

export async function ensureDatabaseUpdated(): Promise<DatabaseUpdateRun> {
  const { data } = await apiClient.post<DatabaseUpdateRun>(API.databaseUpdateEnsure(), null, {
    // A slot whose build predates this endpoint answers 404: let the user in, don't raise the version alarm.
    _skipVersionMismatch: true,
  } as never);
  return data;
}

export async function getDatabaseUpdateStatus(runId?: number): Promise<DatabaseUpdateRun> {
  const { data } = await apiClient.get<DatabaseUpdateRun>(API.databaseUpdateStatus(runId), {
    _skipVersionMismatch: true,
  } as never);
  return data;
}
